import { useState } from 'react';

import { C, FONT_MONO, LABEL_TRACKING, SUBLABEL_TRACKING } from '../lib/design';
import { useDataHealth } from '../lib/data-health';

const STATUS_LABEL = {
  healthy: 'DATA CURRENT',
  attention: 'DATA CHECK',
  limited: 'DATA LIMITED',
} as const;

const DEPENDENCY_LABEL = {
  weight_trend: 'Weight trend',
  nutrition_coaching: 'Nutrition adjustment',
  strength_coaching: 'Strength coaching',
  run_coaching: 'Run progression',
} as const;

const SOURCE_STATUS_LABEL = {
  current: 'CURRENT',
  delayed: 'DELAYED',
  stale: 'STALE',
  missing: 'MISSING',
  error: 'ERROR',
  idle: 'IDLE',
  action_required: 'ACTION NEEDED',
} as const;

export function DataHealthPill({
  compactWhenHealthy = false,
  height = 28,
}: { compactWhenHealthy?: boolean; height?: number } = {}) {
  const [open, setOpen] = useState(false);
  const query = useDataHealth();
  const status = query.data?.status;
  const color = status === 'healthy' ? C.signal : status === 'limited' ? C.red : C.warn;
  const compact = compactWhenHealthy && !query.error && status === 'healthy';

  return (
    <div style={{ position: 'relative', fontFamily: FONT_MONO }}>
      <button
        type="button"
        aria-expanded={open}
        aria-label={compact ? STATUS_LABEL.healthy : undefined}
        title={compact ? STATUS_LABEL.healthy : undefined}
        onClick={() => setOpen((v) => !v)}
        style={{
          border: `1px solid ${query.error ? C.red : color}`,
          background: C.bg,
          color: query.error ? C.red : color,
          // Same box as the header's ◆ COACH button so the two pills align.
          height,
          width: compact ? height : undefined,
          padding: compact ? 0 : '0 10px',
          fontFamily: FONT_MONO,
          fontSize: compact ? 11 : 10,
          letterSpacing: compact ? 0 : SUBLABEL_TRACKING,
          whiteSpace: 'nowrap',
          cursor: 'pointer',
        }}
      >
        {query.isLoading ? 'DATA…' : query.error ? 'DATA UNREACHABLE' : compact ? '●' : `● ${STATUS_LABEL[status!]}`}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 8px)',
            zIndex: 100,
            width: 340,
            maxWidth: 'calc(100vw - 32px)',
            maxHeight: '70vh',
            overflowY: 'auto',
            background: C.bg,
            border: `1px dashed ${query.error ? C.red : color}`,
            padding: 14,
          }}
        >
          {query.error || !query.data ? (
            <div style={{ color: C.red, fontSize: 11, lineHeight: 1.5 }}>
              Data status could not be loaded. Existing values remain visible, but freshness-dependent insights are paused.
            </div>
          ) : (
            <>
              <div style={{ color, fontSize: 10, letterSpacing: LABEL_TRACKING, fontWeight: 700 }}>
                {STATUS_LABEL[query.data.status]}
              </div>
              <div style={{ color: C.dim, fontSize: 11, lineHeight: 1.5, marginTop: 6 }}>
                {query.data.summary}
              </div>
              <div style={{ marginTop: 12, borderTop: `1px solid ${C.line}` }}>
                <div style={{ color: C.faint, fontSize: 9, letterSpacing: LABEL_TRACKING, paddingTop: 10 }}>
                  COACHING READINESS
                </div>
                {query.data.dependencies.map((dependency) => {
                  const dependencyStatus = dependency.status
                    ?? (dependency.ready ? 'ready' : 'paused');
                  const dependencyColor = dependencyStatus === 'ready'
                    ? C.signal
                    : dependencyStatus === 'limited'
                      ? C.warn
                      : C.red;
                  return (
                    <div key={dependency.key} style={{ padding: '9px 0', borderBottom: `1px solid ${C.line}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ color: C.text, fontSize: 11 }}>{DEPENDENCY_LABEL[dependency.key]}</span>
                        <span style={{ color: dependencyColor, fontSize: 9, letterSpacing: SUBLABEL_TRACKING }}>
                          {dependencyStatus.toUpperCase()}
                        </span>
                      </div>
                      <div style={{ color: C.dim, fontSize: 10, lineHeight: 1.4, marginTop: 4 }}>
                        {dependency.detail}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 12, borderTop: `1px solid ${C.line}` }}>
                <div style={{ color: C.faint, fontSize: 9, letterSpacing: LABEL_TRACKING, paddingTop: 10 }}>
                  SUPPORTING SOURCES
                </div>
                {query.data.sources.map((source) => {
                  const sourceColor = source.status === 'current'
                    ? C.signal
                    : source.status === 'error'
                      ? C.red
                      : source.status === 'missing' || source.status === 'stale' || source.status === 'action_required'
                        ? C.warn
                        : C.dim;
                  return (
                    <div key={source.key} style={{ padding: '10px 0', borderBottom: `1px solid ${C.line}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ color: C.text, fontSize: 11 }}>{source.label}</span>
                        <span style={{ color: sourceColor, fontSize: 9, letterSpacing: SUBLABEL_TRACKING }}>
                          {SOURCE_STATUS_LABEL[source.status]}
                        </span>
                      </div>
                      <div style={{ color: C.faint, fontSize: 9, marginTop: 3 }}>
                        {source.provenance}
                        {source.last_recorded_on ? (
                          <span className="num"> · {source.last_recorded_on}</span>
                        ) : null}
                      </div>
                      {source.action && (
                        <div style={{ color: C.dim, fontSize: 10, lineHeight: 1.4, marginTop: 5 }}>
                          {source.action}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
